/**
 * ESCHER FALLS — 配色の着せ替え。雫の使い道。
 *
 * ## なぜ配色なのか
 * 通貨の使い道は、**パズルに一切触れないもの**でなければならない。
 * ヒントや面の解放を売ると、盤面を見ずに進める道を金で買えることになる
 * （CORE-SPEC §3.1 と同じ理由）。配色は解答に何の影響も与えない。
 *
 * ## 各配色が守ること
 * - **彩度を持つのは水だけ。** 石と紙は無彩色に近いまま。
 *   ここを崩すと作品ではなく色替えゲームになる
 * - 石の基準色は**目標より明るく取る**。陰影で必ず暗くなる方向に転ぶので、
 *   そのまま入れると最暗面が下限（#8a8578 相当）を割る
 *   （`render/materials.ts` の BASE_* と同じ事情。だから配色ごとに直接持つ）
 * - **紙は明るいままにする。** 暗い配色（いわゆるダークモード）は、
 *   iOS のステータスバーを黒文字に固定してあるので字が読めなくなる。
 *   入れるなら StatusBar プラグインを足して切り替える必要がある（未対応）
 */

import { defaultStore, type RecordStore } from './records';

const KEY = 'escher-falls/skins/v1';

export interface Skin {
  readonly id: string;
  readonly name: string;
  /** 雫での値段。0 は最初から持っている。 */
  readonly cost: number;
  /** DOM（CSS 変数）と three.js の両方に配る色。 */
  readonly colors: {
    readonly paper: number;
    readonly paperShade: number;
    readonly ink: number;
    readonly inkSoft: number;
    readonly water: number;
    readonly accent: number;
    /** 石のマテリアル基準色。**表示される階調ではなく、陰影前の値。** */
    readonly baseStone: number;
    readonly baseStoneMid: number;
    readonly baseStoneDark: number;
    /** 水のマテリアル基準色。`water` より沈めて、石との対比を作る。 */
    readonly baseWater: number;
  };
}

export const SKINS: readonly Skin[] = [
  {
    id: 'hakushi',
    name: '白紙',
    cost: 0,
    colors: {
      paper: 0xf2efe6, paperShade: 0xe6e2d6, ink: 0x2b2823, inkSoft: 0x6b665c,
      water: 0x4a8c8a, accent: 0xc8622d,
      baseStone: 0xffffff, baseStoneMid: 0xe4e0d5, baseStoneDark: 0xc2bdb0,
      baseWater: 0x3c7c7a,
    },
  },
  {
    id: 'ai',
    name: '藍',
    cost: 200,
    colors: {
      paper: 0xeceef1, paperShade: 0xdde1e7, ink: 0x232833, inkSoft: 0x5f6673,
      water: 0x3f6fa8, accent: 0xb8602f,
      baseStone: 0xffffff, baseStoneMid: 0xe2e4e9, baseStoneDark: 0xbec2ca,
      baseWater: 0x335a8a,
    },
  },
  {
    id: 'koke',
    name: '苔',
    cost: 250,
    colors: {
      paper: 0xf1f0e4, paperShade: 0xe3e2d0, ink: 0x272a20, inkSoft: 0x676a58,
      water: 0x5c8f45, accent: 0xc06a24,
      baseStone: 0xffffff, baseStoneMid: 0xe6e5d6, baseStoneDark: 0xc3c2ae,
      baseWater: 0x4b7a37,
    },
  },
  {
    id: 'shu',
    name: '朱',
    cost: 300,
    colors: {
      paper: 0xf5f0e6, paperShade: 0xe9e2d3, ink: 0x2e2620, inkSoft: 0x6f6558,
      water: 0xb4472f, accent: 0x3f7d7b,
      baseStone: 0xffffff, baseStoneMid: 0xe9e3d5, baseStoneDark: 0xc7c0af,
      baseWater: 0x983826,
    },
  },
  {
    id: 'sumi',
    name: '墨',
    cost: 400,
    colors: {
      paper: 0xeeeeec, paperShade: 0xdededa, ink: 0x1c1c1a, inkSoft: 0x5c5c58,
      water: 0x3d4a52, accent: 0x8a5a3c,
      baseStone: 0xffffff, baseStoneMid: 0xe2e2de, baseStoneDark: 0xbebeb8,
      baseWater: 0x2f3a41,
    },
  },
];

export const DEFAULT_SKIN_ID = 'hakushi';

export function skinById(id: string): Skin {
  return SKINS.find((s) => s.id === id) ?? SKINS[0];
}

export interface SkinState {
  /** 持っている配色の id。既定はいつでも入っている。 */
  readonly owned: readonly string[];
  /** いま着ているもの。 */
  readonly selected: string;
}

export const DEFAULT_SKIN_STATE: SkinState = {
  owned: [DEFAULT_SKIN_ID],
  selected: DEFAULT_SKIN_ID,
};

/** 読み込む。**壊れていても既定に戻すだけで、決して投げない。** */
export function loadSkins(store: RecordStore | null = defaultStore()): SkinState {
  if (store === null) return DEFAULT_SKIN_STATE;
  try {
    const raw = store.getItem(KEY);
    if (raw === null) return DEFAULT_SKIN_STATE;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return DEFAULT_SKIN_STATE;
    const o = parsed as Record<string, unknown>;
    const known = new Set(SKINS.map((s) => s.id));
    // 知らない id は捨てる。版を戻したときに、消えた配色を着たままにしない。
    const owned = Array.isArray(o.owned)
      ? o.owned.filter((x): x is string => typeof x === 'string' && known.has(x))
      : [];
    if (!owned.includes(DEFAULT_SKIN_ID)) owned.push(DEFAULT_SKIN_ID);
    const selected =
      typeof o.selected === 'string' && owned.includes(o.selected)
        ? o.selected
        : DEFAULT_SKIN_ID;
    return { owned, selected };
  } catch {
    return DEFAULT_SKIN_STATE;
  }
}

export function saveSkins(s: SkinState, store: RecordStore | null = defaultStore()): void {
  if (store === null) return;
  try {
    store.setItem(KEY, JSON.stringify({ owned: s.owned, selected: s.selected }));
  } catch {
    // 保存できなくても遊びは続く。
  }
}

/** 持っていない配色を買う。**持ち物への追加だけ**で、雫の支払いは呼び出し側。 */
export function grant(s: SkinState, id: string): SkinState {
  if (s.owned.includes(id)) return s;
  return { ...s, owned: [...s.owned, id] };
}

/** 着替える。持っていないものには着替えられない。 */
export function select(s: SkinState, id: string): SkinState {
  if (!s.owned.includes(id)) return s;
  return { ...s, selected: id };
}
